// /frontend/src/lib/i18n.ts - OPTIMIZED
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enErrors from './locales/en/errors.json';
import esErrors from './locales/es/errors.json';
import LanguageDetector from 'i18next-browser-languagedetector';
import { siteLangForHost, htmlLangFor } from './siteDomain';
import enAppJson from './locales/en/app.json';
import esAppJson from './locales/es/app.json';
import enLandingJson from './locales/en/landing.json';
import esLandingJson from './locales/es/landing.json';
import enBillingJson from './locales/en/billing.json';
import esBillingJson from './locales/es/billing.json';

// Inline resources for public landing/auth pages to prevent Suspense on initial page load
const resources = {
    "en": {
        "translation": {
            "nav_group_main_operations": "MAIN OPERATIONS",
            "nav_group_communications": "COMMUNICATIONS",
            "nav_group_hr": "HUMAN RESOURCES",
            "nav_group_my_account": "MY ACCOUNT",
            "nav_group_main": "MAIN",
            "nav_group_operations": "OPERATIONS",
            "nav_group_properties": "PROPERTIES",
            "nav_group_management": "MANAGEMENT",
            "nav_group_system": "SYSTEM",
            "welcome_back": "Welcome back",
            "sign_in_continue": "Sign in to your account to continue",
            "remember_me": "Remember me",
            "forgot_password": "Forgot your password?",
            "sign_in": "Sign In",
            "signing_in": "Signing in...",
            "no_account": "Don't have an account?",
            "create_one": "Create one now",
            "login_success": "Login successful! Redirecting...",
            "login_error": "Login Error",
            "create_account": "Create Your Account",
            "start_managing": "Start managing your properties with SyncLyst",
            "company_info": "Company Information",
            "company_name": "Company Name",
            "onboarding_company_name_placeholder": "Beach Rentals LLC",
            "company_email": "Company Email",
            "onboarding_company_email_placeholder": "admin@yourcompany.com",
            "business_type": "Business Type",
            "onboarding_phone_placeholder": "+1 (555) 123-4567",
            "onboarding_address_placeholder": "City, State, Country",
            "admin_user": "Admin User",
            "onboarding_admin_first_name_placeholder": "John",
            "onboarding_admin_last_name_placeholder": "Smith",
            "subscription_plan": "Subscription Plan",
            "popular_bundles": "Popular Plans",
            "build_your_own": "Build Your Own",
            "payment_method": "Payment Method",
            "payment_secure": "Your payment info is stored securely by Stripe.",
            "agree_terms": "I agree to the Terms of Service and Privacy Policy",
            "agree_terms_pre": "I agree to the ",
            "agree_terms_tos": "Terms of Service",
            "agree_terms_and": " and ",
            "agree_terms_privacy": "Privacy Policy",
            "create_subscribe": "Create Account & Subscribe",
            "passwords_mismatch": "Passwords do not match.",
            "welcome_email_sent": "We sent a welcome email to {{email}}.",
            "welcome_email_maybe_sent": "Your account is ready. If you don't see an email, check spam/junk.",
            "go_to_login": "Go to Login",
            "back_to_signup": "Back to signup",
            "signup_finish_cta": "Please sign in with the email and password you just created to continue.",
            "account_created_check_email": "Account created! Check your email for next steps.",
            "have_coupon": "Have a discount code?",
            "coupon_placeholder": "Coupon code",
            "coupon_code": "Discount Code",
            "agree_terms_required": "You must agree to the Terms of Service and Privacy Policy.",
            "nav_contact_sales": "Contact Sales",
            "nav_features": "Features",
            "nav_pricing": "Pricing",
            "nav_comparison": "vs BreezeWay",
            "nav_login": "Login",
            "nav_start_trial": "Start Free Trial",
            "hero_badge": "THE FUTURE OF PROPERTY MANAGEMENT",
            "partner_logo_alt": "Partner logo",
            "synclyst_logo_alt": "SyncLyst logo",
            "testimonial_1_name": "Sarah J.",
            "testimonial_1_role": "Property Manager, CA",
            "testimonial_2_name": "Mike T.",
            "testimonial_2_role": "Airbnb Host",
            "testimonial_3_name": "Elena R.",
            "testimonial_3_role": "Operations Director",
            "trusted_by": "Trusted by Property Managers",
            "trusted_desc": "Join the community transforming the industry.",
            "testimonial_1": "SyncLyst changed how we operate. The offline mode is a lifesaver.",
            "testimonial_2": "Finally, a dashboard that looks good and works even better.",
            "testimonial_3": "The automation rules saved us 20 hours a week on scheduling alone.",
            "company_hero_story": "Our Story",
            "company_hero_title_1": "We build software that",
            "company_hero_title_cyan": "powers modern operations.",
            "company_hero_desc": "SyncLyst develops intelligent platforms designed to remove operational friction and help businesses run more efficiently.",
            "company_mission_title": "Our Mission",
            "company_mission_desc": "To build software that simplifies complex operations and empowers teams to work smarter.",
            "company_vision_title": "Our Vision",
            "company_vision_desc": "A future where intelligent software quietly powers everyday operations, allowing people to focus on meaningful work instead of manual processes.",
            "company_principles_title": "Our Principles",
            "company_principle_clarity_title": "Build with clarity",
            "company_principle_clarity_desc": "Software should simplify complexity, not create more of it.",
            "company_principle_humans_title": "Design for humans",
            "company_principle_humans_desc": "Great tools feel natural, intuitive, and enjoyable to use.",
            "company_principle_automation_title": "Automate the boring",
            "company_principle_automation_desc": "Technology should eliminate repetitive work.",
            "company_principle_longterm_title": "Think long term",
            "company_principle_longterm_desc": "We build systems designed to scale and endure.",
            "company_team_collective": "The Engineering Collective",
            "company_team_title": "Meet the Experts",
            "company_team_subtitle": "Behind Your Success",
            "company_team_desc": "We are a distributed team of engineers and designers building powerful software that eliminates operational friction and helps businesses run smarter.",
            "company_team_join": "Join our team",
            "company_category_leadership": "Leadership",
            "company_category_engineering": "Core Engineering",
            "company_category_growth": "Growth",
            "company_role_ceo": "Co-CEO & Founder, Lead Engineer",
            "company_role_coceo": "Co-CEO & Founder, Lead Designer",
            "company_role_partner": "Partner",
            "company_role_frontend": "Frontend Developer",
            "company_role_fullstack": "Fullstack Engineer",
            "company_role_qa": "QA Engineer",
            "company_role_qa_lead": "QA Lead",
            "company_role_marketing_sales": "Marketing & Sales",
            "company_role_community_manager": "Community Manager",
            "company_skill_strategy": "Strategy",
            "company_skill_leadership": "Leadership",
            "company_skill_operations": "Operations",
            "company_skill_web_ui": "Web/UI",
            "company_skill_mobile": "Mobile app",
            "company_skill_testing": "Testing",
            "company_skill_automation": "Automation",
            "company_skill_quality": "Quality",
            "company_skill_marketing": "Marketing",
            "company_skill_sales": "Sales",
            "company_skill_community": "Community",
            "company_skill_social_media": "Social Media",
            "company_status_pending": "Pending",
            "company_status_coming_soon": "Coming Soon",
            "company_pending_name": "Pending Name",
            "company_team_fernando": "Fernando Avila",
            "company_team_david": "David Wallace",
            "company_team_raul": "Raul Martinez",
            "company_team_ricardo": "Ricardo Belmont",
            "nav_solutions": "Solutions",
            "nav_company": "Company",
            "terms_title": "Terms of Service",
            "terms_last_updated": "Last updated: December 13, 2024",
            "terms_back_home": "Back to Home",
            "privacy_title": "Privacy Policy",
            "privacy_last_updated": "Last updated: March 7, 2026",
            "privacy_intro_title": "Introduction",
            "faq_title": "Frequently Asked Questions",
            "faq_1_q": "How is pricing determined?",
            "faq_1_a": "Pricing is custom-tailored based on the number of properties in your portfolio and your specific operational needs.",
            "faq_2_q": "Is there a long-term contract?",
            "faq_2_a": "We offer both annual contracts and flexible options. Contact us to find the best fit for your business.",
            "faq_3_q": "Do you offer enterprise workflows and integrations?",
            "faq_3_a": "Absolutely. We provide dedicated support, custom data migrations, and advanced integrations for large-scale operations.",
            "contact_title": "Let's Talk",
            "contact_subtitle": "We'd love to hear from you and show you how SyncLyst can transform your property management operations.",
            "contact_form_cta": "Get in Touch",
            "contact_help_text": "Fill out this quick form or contact us directly. We're here to help!",
            "contact_name": "Name",
            "contact_name_placeholder": "Enter your name",
            "contact_email": "Email",
            "contact_email_placeholder": "Enter your email",
            "contact_company": "Company (Optional)",
            "contact_company_placeholder": "Your company name",
            "contact_phone": "Phone (Optional)",
            "contact_phone_placeholder": "Your phone number",
            "contact_message": "How can we help you?",
            "contact_message_placeholder": "Tell us about your property management needs...",
            "contact_submit": "Send Message",
            "contact_sending": "Sending...",
            "contact_success": "Message sent successfully! We'll get back to you soon.",
            "contact_error": "Failed to send message. Please try again or email us directly.",
            "contact_email_direct": "contact@synclyst.com",
            "footer_rights": "All rights reserved.",
            "header_ai_assistant": "AI Assistant",
            "header_ai_assistant_desc": "Show floating AI button",
            "footer_privacy": "Privacy Policy",
            "footer_terms": "Terms of Service"
        }
    },
    "es": {
        "translation": {
            "nav_group_main_operations": "OPERACIONES PRINCIPALES",
            "nav_group_communications": "COMUNICACIONES",
            "nav_group_hr": "RECURSOS HUMANOS",
            "nav_group_my_account": "MI CUENTA",
            "nav_group_main": "PRINCIPAL",
            "nav_group_operations": "OPERACIONES",
            "nav_group_properties": "PROPIEDADES",
            "nav_group_management": "GESTIÓN",
            "nav_group_system": "SISTEMA",
            "welcome_back": "Bienvenido de nuevo",
            "sign_in_continue": "Inicia sesión para continuar",
            "remember_me": "Recuérdame",
            "forgot_password": "¿Olvidaste tu contraseña?",
            "sign_in": "Iniciar Sesión",
            "signing_in": "Iniciando sesión...",
            "no_account": "¿No tienes una cuenta?",
            "create_one": "Crea una ahora",
            "login_success": "¡Inicio de sesión exitoso! Redirigiendo...",
            "login_error": "Error de inicio de sesión",
            "create_account": "Crea tu Cuenta",
            "start_managing": "Comienza a gestionar tus propiedades con SyncLyst",
            "company_info": "Información de la Empresa",
            "company_name": "Nombre de la Empresa",
            "onboarding_company_name_placeholder": "Rentals Playa S.A.",
            "company_email": "Email de la Empresa",
            "onboarding_company_email_placeholder": "admin@tuempresa.com",
            "admin_user": "Usuario Administrador",
            "onboarding_phone_placeholder": "+1 (555) 123-4567",
            "onboarding_address_placeholder": "Ciudad, Estado, País",
            "onboarding_admin_first_name_placeholder": "Juan",
            "onboarding_admin_last_name_placeholder": "Pérez",
            "subscription_plan": "Plan de Suscripción",
            "popular_bundles": "Planes Populares",
            "build_your_own": "Arma tu Plan",
            "business_type": "Tipo de Empresa",
            "payment_method": "Método de Pago",
            "payment_secure": "Tu información de pago se almacena de forma segura por Stripe.",
            "agree_terms": "Acepto los Términos de Servicio y la Política de Privacidad",
            "agree_terms_pre": "Acepto los ",
            "agree_terms_tos": "Términos de Servicio",
            "agree_terms_and": " y la ",
            "agree_terms_privacy": "Política de Privacidad",
            "agree_terms_required": "Debes aceptar los Términos de Servicio y la Política de Privacidad.",
            "create_subscribe": "Crear Cuenta y Suscribirse",
            "passwords_mismatch": "Las contraseñas no coinciden.",
            "welcome_email_sent": "Hemos enviado un correo de bienvenida a {{email}}.",
            "welcome_email_maybe_sent": "Tu cuenta está lista. Si no ves el correo, revisa tu bandeja de spam/correo no deseado.",
            "go_to_login": "Ir a Iniciar Sesión",
            "back_to_signup": "Volver al registro",
            "signup_finish_cta": "Por favor inicia sesión con el correo y la contraseña que acabas de crear para continuar.",
            "account_created_check_email": "¡Cuenta creada! Revisa tu correo para los siguientes pasos.",
            "have_coupon": "¿Tienes un código de descuento?",
            "coupon_placeholder": "Código de cupón",
            "coupon_code": "Código de Descuento",
            "nav_contact_sales": "Contactar Ventas",
            "nav_features": "Funcionalidades",
            "nav_pricing": "Precios",
            "nav_comparison": "vs BreezeWay",
            "nav_login": "Iniciar Sesión",
            "nav_start_trial": "Prueba Gratis",
            "hero_badge": "EL FUTURO DE LA GESTIÓN DE PROPIEDADES",
            "partner_logo_alt": "Logo de socio",
            "synclyst_logo_alt": "Logo de SyncLyst",
            "testimonial_1_name": "Sarah J.",
            "testimonial_1_role": "Gerente de propiedades, CA",
            "testimonial_2_name": "Mike T.",
            "testimonial_2_role": "Anfitrión de Airbnb",
            "testimonial_3_name": "Elena R.",
            "testimonial_3_role": "Directora de operaciones",
            "trusted_by": "Confían en Nosotros",
            "trusted_desc": "Únete a la comunidad que transforma la industria.",
            "testimonial_1": "SyncLyst cambió nuestra operación. El modo offline es un salvavidas.",
            "testimonial_2": "Finalmente, un panel que se ve bien y funciona aún mejor.",
            "testimonial_3": "Las reglas de automatización nos ahorraron 20 horas a la semana.",
            "company_hero_story": "Nuestra Historia",
            "company_hero_title_1": "Construimos software que",
            "company_hero_title_cyan": "potencia operaciones modernas.",
            "company_hero_desc": "SyncLyst desarrolla plataformas inteligentes diseñadas para eliminar la fricción operativa y ayudar a las empresas a funcionar de manera más eficiente.",
            "company_mission_title": "Nuestra Misión",
            "company_mission_desc": "Construir software que simplifique operaciones complejas y empodere a los equipos para trabajar de manera más inteligente.",
            "company_vision_title": "Nuestra Visión",
            "company_vision_desc": "Un futuro donde el software inteligente impulse silenciosamente las operaciones diarias, permitiendo que las personas se enfoquen en trabajos significativos en lugar de procesos manuales.",
            "company_principles_title": "Nuestros Principios",
            "company_principle_clarity_title": "Construir con claridad",
            "company_principle_clarity_desc": "El software debe simplificar la complejidad, no crear más de ella.",
            "company_principle_humans_title": "Diseñar para humanos",
            "company_principle_humans_desc": "Las grandes herramientas se sienten naturales, intuitivas y agradables de usar.",
            "company_principle_automation_title": "Automatizar lo aburrido",
            "company_principle_automation_desc": "La tecnología debe eliminar el trabajo repetitivo.",
            "company_principle_longterm_title": "Pensar a largo plazo",
            "company_principle_longterm_desc": "Construimos sistemas diseñados para escalar y perdurar.",
            "company_team_collective": "El Colectivo de Ingeniería",
            "company_team_title": "Conoce a los Expertos",
            "company_team_subtitle": "Detrás de tu Éxito",
            "company_team_desc": "Somos un equipo distribuido de ingenieros y diseñadores que construyen software potente que elimina la fricción operativa y ayuda a las empresas a funcionar de manera más inteligente.",
            "company_team_join": "Únete a nuestro equipo",
            "company_category_leadership": "Liderazgo",
            "company_category_engineering": "Ingeniería Principal",
            "company_category_growth": "Crecimiento",
            "company_role_ceo": "Co-CEO & Fundador, Ingeniero Principal",
            "company_role_coceo": "Co-CEO & Fundador, Diseñador Principal",
            "company_role_partner": "Socio",
            "company_role_frontend": "Desarrollador Frontend",
            "company_role_fullstack": "Ingeniero Fullstack",
            "company_role_qa": "Ingeniero de QA",
            "company_role_qa_lead": "QA Lead",
            "company_role_marketing_sales": "Marketing y Ventas",
            "company_role_community_manager": "Community Manager",
            "company_skill_strategy": "Estrategia",
            "company_skill_leadership": "Liderazgo",
            "company_skill_operations": "Operaciones",
            "company_skill_web_ui": "Web/UI",
            "company_skill_mobile": "App Móvil",
            "company_skill_testing": "Pruebas",
            "company_skill_automation": "Automatización",
            "company_skill_quality": "Calidad",
            "company_skill_marketing": "Marketing",
            "company_skill_sales": "Ventas",
            "company_skill_community": "Comunidad",
            "company_skill_social_media": "Redes Sociales",
            "company_status_pending": "Pendiente",
            "company_status_coming_soon": "Próximamente",
            "company_pending_name": "Nombre Pendiente",
            "company_team_fernando": "Fernando Avila",
            "company_team_david": "David Wallace",
            "company_team_raul": "Raul Martinez",
            "company_team_ricardo": "Ricardo Belmont",
            "nav_solutions": "Soluciones",
            "nav_company": "Empresa",
            "terms_title": "Términos de Servicio",
            "terms_last_updated": "Última actualización: 13 de diciembre de 2024",
            "terms_back_home": "Volver al Inicio",
            "privacy_title": "Política de Privacidad",
            "privacy_last_updated": "Última actualización: 7 de marzo de 2026",
            "privacy_intro_title": "Introducción",
            "faq_title": "Preguntas Frecuentes",
            "faq_1_q": "¿Cómo se determina el precio?",
            "faq_1_a": "El precio se personaliza según el número de propiedades en su portafolio y sus necesidades operativas específicas.",
            "faq_2_q": "¿Hay un contrato a largo plazo?",
            "faq_2_a": "Ofrecemos tanto contratos anuales como opciones flexibles. Contáctenos para encontrar la mejor opción para su negocio.",
            "faq_3_q": "¿Ofrecen flujos de trabajo e integraciones empresariales?",
            "faq_3_a": "Absolutamente. Brindamos soporte dedicado, migraciones de datos personalizadas y acceso a integraciones avanzadas para operaciones a gran escala.",
            "contact_title": "Hablemos",
            "contact_subtitle": "Nos encantaría hablar contigo y mostrarte cómo SyncLyst puede transformar la gestión de tus propiedades.",
            "contact_form_cta": "Contáctanos",
            "contact_help_text": "Completa este formulario rápido o contáctanos directamente. ¡Estamos aquí para ayudarte!",
            "contact_name": "Nombre",
            "contact_name_placeholder": "Ingresa tu nombre",
            "contact_email": "Email",
            "contact_email_placeholder": "Ingresa tu email",
            "contact_company": "Empresa (Opcional)",
            "contact_company_placeholder": "Nombre de tu empresa",
            "contact_phone": "Teléfono (Opcional)",
            "contact_phone_placeholder": "Tu número de teléfono",
            "contact_message": "¿Cómo podemos ayudarte?",
            "contact_message_placeholder": "Cuéntanos sobre tus necesidades de gestión de propiedades...",
            "contact_submit": "Enviar Mensaje",
            "header_ai_assistant": "Asistente IA",
            "header_ai_assistant_desc": "Mostrar botón flotante de IA",
            "contact_sending": "Enviando...",
            "contact_success": "¡Mensaje enviado exitosamente! Te responderemos pronto.",
            "contact_error": "Error al enviar el mensaje. Intenta de nuevo o envíanos un correo directo.",
            "contact_email_direct": "contact@synclyst.com",
            "footer_rights": "Todos los derechos reservados.",
            "footer_privacy": "Política de Privacidad",
            "footer_terms": "Términos de Servicio"
        }
    }
};

resources.en.translation = { ...resources.en.translation, ...(enAppJson as Record<string, string>) };
resources.es.translation = { ...resources.es.translation, ...(esAppJson as Record<string, string>) };

// ---- Live Operations Wall translations (inline, always loaded) ----
const enLiveWall: Record<string, string> = {
  'liveWall.brand_badge': 'WAR ROOM KIOSK',
  'liveWall.live': 'LIVE',
  'liveWall.loading': 'Loading…',
  'liveWall.clock_hint': 'Clock',
  'liveWall.kpi_properties': 'Properties',
  'liveWall.kpi_properties_total': '{{n}} Total',
  'liveWall.kpi_properties_idle': '{{n}} Idle',
  'liveWall.kpi_bookings': "Today's Bookings",
  'liveWall.kpi_bookings_in': '{{n}} In',
  'liveWall.kpi_bookings_out': '🚪 {{n}} Out',
  'liveWall.kpi_tasks': 'Task Breakdown',
  'liveWall.kpi_tasks_total': '{{n}} Tasks',
  'liveWall.kpi_tasks_completed': '✓ {{n}}',
  'liveWall.kpi_tasks_in_progress': '{{n}}',
  'liveWall.kpi_tasks_assigned': '{{n}}',
  'liveWall.kpi_tasks_urgent': '{{n}}',
  'liveWall.kpi_tickets': 'Ticket Breakdown',
  'liveWall.kpi_tickets_total': '{{n}} Tickets',
  'liveWall.kpi_tickets_resolved': '✓ {{n}}',
  'liveWall.kpi_tickets_in_progress': '{{n}}',
  'liveWall.kpi_tickets_open': '{{n}}',
  'liveWall.kpi_tickets_urgent': '{{n}}',
  'liveWall.kpi_team': 'Team Status',
  'liveWall.kpi_team_online': '{{n}}/{{total}} Online',
  'liveWall.kpi_team_working': '{{n}}',
  'liveWall.kpi_team_available': '{{n}}',
  'liveWall.kpi_team_offline': '{{n}}',
  'liveWall.kpi_avg_urgent_title': 'Avg Urgent Response',
  'liveWall.kpi_avg_urgent_value': '{{minutes}}m',
  'liveWall.hint_tasks_completed': '✅ Completed today — tasks finished today',
  'liveWall.hint_tasks_in_progress': '🟢 In Progress — actively being worked on right now',
  'liveWall.hint_tasks_assigned': '🟡 Assigned Today — scheduled tasks with assignee for TODAY only',
  'liveWall.hint_tasks_urgent': '🔴 Urgent / Overdue — unassigned past-due tasks & booking alerts',
  'liveWall.hint_tickets_resolved': '✅ Resolved today — support tickets closed today',
  'liveWall.hint_tickets_in_progress': '🟢 In Progress — tickets currently being handled',
  'liveWall.hint_tickets_open': '🟡 Open — new / pending tickets awaiting assignment',
  'liveWall.hint_tickets_urgent': '🔴 Urgent — high-priority open tickets',
  'liveWall.conn_ok': 'Connection OK',
  'liveWall.conn_bad': '⚠️ No connection',
  'liveWall.conn_hace': '{{n}}s ago',
  'liveWall.btn_tv4k': 'TV 4K',
  'liveWall.btn_fullscreen_enter': 'Fullscreen',
  'liveWall.btn_fullscreen_exit': 'Exit Fullscreen',
  'liveWall.btn_exit': 'Exit',
  'liveWall.panel_properties_title': 'Properties with Activity ({{n}})',
  'liveWall.panel_properties_filter_hint': 'Click to filter',
  'liveWall.panel_properties_search_ph': 'Search by name or address...',
  'liveWall.chip_all': 'All',
  'liveWall.chip_urgent_only': 'Urgent Only',
  'liveWall.chip_checkin_today': 'Check-in Today',
  'liveWall.chip_with_tickets': 'With Tickets',
  'liveWall.unassigned_title': '{{n}} TASKS / ALERTS UNASSIGNED',
  'liveWall.unassigned_action': 'ACTION REQUIRED',
  'liveWall.unassigned_assign': 'Assign',
  'liveWall.unassigned_open_ticket': 'Open ticket',
  'liveWall.unassigned_scheduled': 'Scheduled',
  'liveWall.badge_urgent': 'URGENT',
  'liveWall.checkin_today': '🛏️ Check-IN Today',
  'liveWall.checkout_today': '🚪 Check-OUT Today',
  'liveWall.current_stays': '👥 {{n}} Current stay',
  'liveWall.current_stays_plural': '👥 {{n}} Current stays',
  'liveWall.guest_count': '👥 {{n}} Guest',
  'liveWall.guest_count_plural': '👥 {{n}} Guests',
  'liveWall.task_in_progress': '{{n}} In Progress',
  'liveWall.task_assigned': '🔧 {{n}} Assigned',
  'liveWall.task_unassigned': '⚠️ {{n}} Unassigned',
  'liveWall.task_completed_today': '✅ {{n}} Completed today',
  'liveWall.tickets_summary': '🎟️ Tickets: {{open}} Open',
  'liveWall.tickets_urgent_suffix': ' ({{n}} URG)',
  'liveWall.empty_properties': '🌙 No properties match these filters.',
  'liveWall.panel_kanban_title': 'Live Operations Board',
  'liveWall.kanban_filter_on': 'Filtering: {{name}}',
  'liveWall.kanban_filter_off': 'Showing all properties',
  'liveWall.kanban_filter_reset': '[✕ Remove Filter]',
  'liveWall.kanban_filter_reset_title': 'Click to reset filter and show all properties',
  'liveWall.col_urgent': 'Urgent ({{n}})',
  'liveWall.col_assigned': 'Assigned ({{n}})',
  'liveWall.col_in_progress': 'In Progress ({{n}})',
  'liveWall.col_completed': 'Completed ({{n}})',
  'liveWall.col_empty': '— None —',
  'liveWall.card_kind_task': 'TASK',
  'liveWall.card_kind_ticket': 'TICKET',
  'liveWall.card_kind_booking': 'BOOKING ALERT',
  'liveWall.card_kind_session': 'SESSION',
  'liveWall.card_priority_urgent': 'URGENT',
  'liveWall.card_priority_high': 'HIGH',
  'liveWall.card_priority_medium': 'MEDIUM',
  'liveWall.card_priority_low': 'LOW',
  'liveWall.card_checkin_now': 'Check-in in {{h}}h {{m}}m',
  'liveWall.card_checkin_late': 'Check-in overdue {{h}}h {{m}}m',
  'liveWall.card_turnover_assigned': '✅ Turnover assigned',
  'liveWall.card_turnover_missing': '⚠️ TURNOVER CLEAN: NOT ASSIGNED (Urgent)',
  'liveWall.card_progress_avance': 'Progress:',
  'liveWall.card_progress_checklist': 'Checklist: {{pct}}%',
  'liveWall.card_progress_step': 'In progress',
  'liveWall.card_not_assigned': 'Not assigned',
  'liveWall.card_starts_at': 'Start: ',
  'liveWall.card_due_in_m': '⏳ {{n}}m',
  'liveWall.card_due_in_h': '⏳ {{h}}h {{m}}m',
  'liveWall.card_overdue_m': '⏰ Overdue {{n}}m',
  'liveWall.card_overdue_h': '⏰ Overdue {{h}}h',
  'liveWall.card_assign_now': '🚨 ASSIGN NOW',
  'liveWall.card_assign_personal': 'Assign Personnel',
  'liveWall.panel_team_title': 'Live Team Pulse',
  'liveWall.panel_team_online': '{{n}} Online',
  'liveWall.team_working': 'Active Work ({{n}})',
  'liveWall.team_working_empty': 'No one on active tasks.',
  'liveWall.team_working_badge': 'WORKING',
  'liveWall.team_started': ' • Started',
  'liveWall.team_role_fallback': 'Team member',
  'liveWall.team_task_summary': '🔧 {{prop}} — {{title}}',
  'liveWall.team_available': 'Available / Online ({{n}})',
  'liveWall.team_available_empty': '— No team members available —',
  'liveWall.team_avail_now': '✅ AVAILABLE NOW',
  'liveWall.team_avail_later': '🟡 AVAILABLE',
  'liveWall.team_avail_activity_justnow': ' • Activity 3 min ago',
  'liveWall.team_avail_activity_before': ' • Avail. {{t}}',
  'liveWall.team_avail_queue_warning': '⚠️ {{n}} unassigned tasks in queue',
  'liveWall.team_offline_title': 'Offline today ({{n}})',
  'liveWall.team_offline_empty': 'All team members are active.',
  'liveWall.team_offline_last': 'Last seen',
  'liveWall.team_task_elapsed_label': 'On task',
  'liveWall.team_idle_label': 'Idle {{t}}',
  'liveWall.team_working_total': 'Total: {{t}}',
  'liveWall.team_available_avg_idle': 'Avg idle: {{t}}',
  'liveWall.team_working_pin_label': 'PINNED',
  'liveWall.error_title': 'Something went wrong',
  'liveWall.error_body': 'Unable to load the live wall. Retrying automatically.',
  'liveWall.error_retry': 'Retry Now',
  'liveWall.loading_title': 'Loading War Room…',
  'liveWall.cta_header': 'SEE LIVE DASHBOARD',
  'liveWall.cta_header_badge': 'LIVE',
  'liveWall.page_title': 'SyncLyst — Live Operations Wall',
  'liveWall.toast_read_only_mode': 'Live Wall is read-only. Open the Tasks page to assign team members.',
};

const esLiveWall: Record<string, string> = {
  'liveWall.brand_badge': 'WAR ROOM KIOSK',
  'liveWall.live': 'LIVE',
  'liveWall.loading': 'Cargando…',
  'liveWall.clock_hint': 'Reloj',
  'liveWall.kpi_properties': 'Propiedades',
  'liveWall.kpi_properties_total': '{{n}} Total',
  'liveWall.kpi_properties_idle': '{{n}} Idle',
  'liveWall.kpi_bookings': 'Bookings de Hoy',
  'liveWall.kpi_bookings_in': '{{n}} In',
  'liveWall.kpi_bookings_out': '🚪 {{n}} Out',
  'liveWall.kpi_tasks': 'Desglose de Tareas',
  'liveWall.kpi_tasks_total': '{{n}} Tareas',
  'liveWall.kpi_tasks_completed': '✓ {{n}}',
  'liveWall.kpi_tasks_in_progress': '{{n}}',
  'liveWall.kpi_tasks_assigned': '{{n}}',
  'liveWall.kpi_tasks_urgent': '{{n}}',
  'liveWall.kpi_tickets': 'Desglose de Tickets',
  'liveWall.kpi_tickets_total': '{{n}} Tickets',
  'liveWall.kpi_tickets_resolved': '✓ {{n}}',
  'liveWall.kpi_tickets_in_progress': '{{n}}',
  'liveWall.kpi_tickets_open': '{{n}}',
  'liveWall.kpi_tickets_urgent': '{{n}}',
  'liveWall.kpi_team': 'Estado de Equipo',
  'liveWall.kpi_team_online': '{{n}}/{{total}} Online',
  'liveWall.kpi_team_working': '{{n}}',
  'liveWall.kpi_team_available': '{{n}}',
  'liveWall.kpi_team_offline': '{{n}}',
  'liveWall.kpi_avg_urgent_title': 'Resp. Urgente',
  'liveWall.kpi_avg_urgent_value': '{{minutes}}m',
  'liveWall.hint_tasks_completed': '✅ Completadas hoy — tareas finalizadas en el día de hoy',
  'liveWall.hint_tasks_in_progress': '🟢 En Progreso — trabajando en este momento',
  'liveWall.hint_tasks_assigned': '🟡 Asignadas HOY — tareas programadas CON responsable SOLO para HOY',
  'liveWall.hint_tasks_urgent': '🔴 Urgentes / Vencidas — tareas pasadas sin asignar y alertas de reservación',
  'liveWall.hint_tickets_resolved': '✅ Resueltos hoy — tickets de soporte cerrados hoy',
  'liveWall.hint_tickets_in_progress': '🟢 En Progreso — tickets en atención actualmente',
  'liveWall.hint_tickets_open': '🟡 Abiertos — tickets nuevos / pendientes por asignar',
  'liveWall.hint_tickets_urgent': '🔴 Urgentes — tickets abiertos de prioridad alta',
  'liveWall.conn_ok': 'Conexión OK',
  'liveWall.conn_bad': '⚠️ Sin conexión',
  'liveWall.conn_hace': 'hace {{n}}s',
  'liveWall.btn_tv4k': 'TV 4K',
  'liveWall.btn_fullscreen_enter': 'Pantalla Completa',
  'liveWall.btn_fullscreen_exit': 'Salir Pant. Completa',
  'liveWall.btn_exit': 'Salir',
  'liveWall.panel_properties_title': 'Propiedades con Actividad ({{n}})',
  'liveWall.panel_properties_filter_hint': 'Click para filtrar',
  'liveWall.panel_properties_search_ph': 'Buscar por nombre o dirección...',
  'liveWall.chip_all': 'Todos',
  'liveWall.chip_urgent_only': 'Solo Urgentes',
  'liveWall.chip_checkin_today': 'Check-in Hoy',
  'liveWall.chip_with_tickets': 'Con Tickets',
  'liveWall.unassigned_title': '{{n}} TAREAS / ALERTAS SIN ASIGNAR',
  'liveWall.unassigned_action': 'ACCIÓN REQUERIDA',
  'liveWall.unassigned_assign': 'Asignar',
  'liveWall.unassigned_open_ticket': 'Ticket abierto',
  'liveWall.unassigned_scheduled': 'Programada',
  'liveWall.badge_urgent': 'URGENTE',
  'liveWall.checkin_today': '🛏️ Check-IN Hoy',
  'liveWall.checkout_today': '🚪 Check-OUT Hoy',
  'liveWall.current_stays': '👥 {{n}} Estancia actual',
  'liveWall.current_stays_plural': '👥 {{n}} Estancias actuales',
  'liveWall.guest_count': '👥 {{n}} Huésped',
  'liveWall.guest_count_plural': '👥 {{n}} Huéspedes',
  'liveWall.task_in_progress': '{{n}} En Progreso',
  'liveWall.task_assigned': '🔧 {{n}} Asignadas',
  'liveWall.task_unassigned': '⚠️ {{n}} Sin Asignar',
  'liveWall.task_completed_today': '✅ {{n}} Completadas hoy',
  'liveWall.tickets_summary': '🎟️ Tickets: {{open}} Abiertos',
  'liveWall.tickets_urgent_suffix': ' ({{n}} URG)',
  'liveWall.empty_properties': '🌙 Sin propiedades con estos filtros.',
  'liveWall.panel_kanban_title': 'Tablero de Operaciones en Vivo',
  'liveWall.kanban_filter_on': 'Filtrando: {{name}}',
  'liveWall.kanban_filter_off': 'Mostrando todas las propiedades',
  'liveWall.kanban_filter_reset': '[✕ Quitar Filtro]',
  'liveWall.kanban_filter_reset_title': 'Click para quitar filtro y mostrar todas las propiedades',
  'liveWall.col_urgent': 'Urgente ({{n}})',
  'liveWall.col_assigned': 'Asignado ({{n}})',
  'liveWall.col_in_progress': 'En Progreso ({{n}})',
  'liveWall.col_completed': 'Completado ({{n}})',
  'liveWall.col_empty': '— Ninguno —',
  'liveWall.card_kind_task': 'TAREA',
  'liveWall.card_kind_ticket': 'TICKET',
  'liveWall.card_kind_booking': 'ALERTA BOOKING',
  'liveWall.card_kind_session': 'SESIÓN',
  'liveWall.card_priority_urgent': 'URGENTE',
  'liveWall.card_priority_high': 'ALTA',
  'liveWall.card_priority_medium': 'MEDIA',
  'liveWall.card_priority_low': 'BAJA',
  'liveWall.card_checkin_now': 'Check-in en {{h}}h {{m}}m',
  'liveWall.card_checkin_late': 'Check-in atrasado {{h}}h {{m}}m',
  'liveWall.card_turnover_assigned': '✅ Turnover asignado',
  'liveWall.card_turnover_missing': '⚠️ TURNOVER CLEAN: SIN ASIGNAR (Urgente)',
  'liveWall.card_progress_avance': 'Avance:',
  'liveWall.card_progress_checklist': 'Checklist: {{pct}}%',
  'liveWall.card_progress_step': 'En ejecución',
  'liveWall.card_not_assigned': 'Sin asignar',
  'liveWall.card_starts_at': 'Inicio: ',
  'liveWall.card_due_in_m': '⏳ {{n}}m',
  'liveWall.card_due_in_h': '⏳ {{h}}h {{m}}m',
  'liveWall.card_overdue_m': '⏰ Atrasada {{n}}m',
  'liveWall.card_overdue_h': '⏰ Atrasada {{h}}h',
  'liveWall.card_assign_now': '🚨 ASIGNAR AHORA',
  'liveWall.card_assign_personal': 'Asignar Personal',
  'liveWall.panel_team_title': 'Team Pulse en Vivo',
  'liveWall.panel_team_online': '{{n}} Online',
  'liveWall.team_working': 'En Trabajo Activo ({{n}})',
  'liveWall.team_working_empty': 'Nadie en tareas activas.',
  'liveWall.team_working_badge': 'WORKING',
  'liveWall.team_started': ' • Inició',
  'liveWall.team_role_fallback': 'Colaborador',
  'liveWall.team_task_summary': '🔧 {{prop}} — {{title}}',
  'liveWall.team_available': 'Disponible / Online ({{n}})',
  'liveWall.team_available_empty': '— Sin colaboradores disponibles —',
  'liveWall.team_avail_now': '✅ DISPONIBLE AHORA',
  'liveWall.team_avail_later': '🟡 DISPONIBLE',
  'liveWall.team_avail_activity_justnow': ' • Actividad hace 3 min',
  'liveWall.team_avail_activity_before': ' • Disp. hace {{t}}',
  'liveWall.team_avail_queue_warning': '⚠️ {{n}} tareas sin asignar en cola',
  'liveWall.team_offline_title': 'Offline hoy ({{n}})',
  'liveWall.team_offline_empty': 'Todos los colaboradores activos.',
  'liveWall.team_offline_last': 'Última conexión',
  'liveWall.team_task_elapsed_label': 'En task',
  'liveWall.team_idle_label': 'Disponible {{t}}',
  'liveWall.team_working_total': 'Total trabajado: {{t}}',
  'liveWall.team_available_avg_idle': 'Prom. disp: {{t}}',
  'liveWall.team_working_pin_label': 'FIJADO',
  'liveWall.error_title': 'Algo salió mal',
  'liveWall.error_body': 'No se pudo cargar el muro en vivo. Reintentando automáticamente.',
  'liveWall.error_retry': 'Reintentar Ahora',
  'liveWall.loading_title': 'Cargando War Room…',
  'liveWall.cta_header': 'SEE LIVE DASHBOARD',
  'liveWall.cta_header_badge': 'LIVE',
  'liveWall.page_title': 'SyncLyst — Muro Operaciones en Vivo',
  'liveWall.toast_read_only_mode':
    'El Live Wall es solo lectura. Abre la página de Tareas para asignar personal.',
};

Object.entries(enLiveWall).forEach(([k, v]) => { (resources.en.translation as Record<string, string>)[k] = v; });
Object.entries(esLiveWall).forEach(([k, v]) => { (resources.es.translation as Record<string, string>)[k] = v; });
// ---- END Live Wall translations ----

// Catalogo de errores (ns 'errors', clave errors.<code>): pequeno, se carga con el bundle inicial.
(resources.en as Record<string, unknown>).errors = enErrors;
(resources.es as Record<string, unknown>).errors = esErrors;
// RF-41: claves landing_* en locales/{en,es}/landing.json (carga estatica: la landing no espera al lazy load).
Object.assign(resources.en.translation, enLandingJson);
Object.assign(resources.es.translation, esLandingJson);
// RF-38: namespace billing (locales/{en,es}/billing.json), claves anidadas snake_case.
const withBilling = resources as Record<string, Record<string, unknown>>;
withBilling.en.billing = enBillingJson;
withBilling.es.billing = esBillingJson;

// Idioma por dominio (synclyst.mx → es, synclyst.com → en) cuando no hay ?lng ni preferencia guardada.
const siteDetector = new LanguageDetector();
siteDetector.addDetector({ name: 'siteDomain', lookup: () => siteLangForHost() });

i18n
    .use(siteDetector)
    .use(initReactI18next)
    .init({
        resources,
        ns: ['translation', 'errors'],
        defaultNS: 'translation',
        fallbackLng: 'en',
        // Namespace 'condo' (src/lib/locales/*/condo.json) se agrega en diferido desde modules/condo/i18n.ts.
        fallbackNS: 'condo',
        interpolation: {
            escapeValue: false // react already safes from xss
        },
        detection: {
            order: ['querystring', 'localStorage', 'siteDomain', 'navigator'],
            lookupQuerystring: 'lng',
            caches: ['localStorage']
        }
    });

// Dynamically load the rest of the application translations in the background
// so they do not bloat the initial landing page Javascript bundle.
const loadLazyTranslations = async () => {
    try {
        const [enApp, esApp] = await Promise.all([
            import('./locales/en/app.json'),
            import('./locales/es/app.json')
        ]);
        
        i18n.addResourceBundle('en', 'translation', enApp.default, true, true);
        i18n.addResourceBundle('es', 'translation', esApp.default, true, true);

        // Catálogo único de errores (T1): namespace 'errors' (errors.*, alerts.*, warnings.*, status.*, common.*, enums.*)
        const [enErrors, esErrors] = await Promise.all([
            import('./locales/en/errors.json'),
            import('./locales/es/errors.json')
        ]);
        i18n.addResourceBundle('en', 'errors', enErrors.default, true, true);
        i18n.addResourceBundle('es', 'errors', esErrors.default, true, true);
        
    } catch {
        // sin traducciones diferidas: se usan las incluidas en el bundle inicial
    }
};

if (typeof window !== 'undefined') {
    const isLandingPage = window.location.pathname === '/';
    
    if (!isLandingPage) {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => loadLazyTranslations());
        } else {
            loadLazyTranslations();
        }
    } else {
        let loaded = false;
        const triggerLazyLoad = () => {
            if (loaded) return;
            loaded = true;
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => loadLazyTranslations());
            } else {
                loadLazyTranslations();
            }
            cleanup();
        };

        const interactionEvents = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
        
        const cleanup = () => {
            interactionEvents.forEach(evt => {
                window.removeEventListener(evt, triggerLazyLoad, { capture: true });
            });
            document.removeEventListener('mouseenter', linkInteractionListener, true);
            document.removeEventListener('focusin', linkInteractionListener, true);
        };

        const linkInteractionListener = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target && (
                target.tagName === 'A' && (
                    target.getAttribute('href')?.includes('/login') || 
                    target.getAttribute('href')?.includes('/register') ||
                    target.getAttribute('href')?.includes('/onboarding')
                )
            )) {
                triggerLazyLoad();
            }
        };

        // Add page interaction listeners
        interactionEvents.forEach(evt => {
            window.addEventListener(evt, triggerLazyLoad, { capture: true, passive: true });
        });

        // Add link hover/focus listeners
        document.addEventListener('mouseenter', linkInteractionListener, true);
        document.addEventListener('focusin', linkInteractionListener, true);
    }
} else {
    loadLazyTranslations();
}

// Mantiene <html lang> sincronizado con el idioma activo (a11y/SEO).
const syncHtmlLang = (lng?: string) => {
    if (typeof document === 'undefined' || !lng) return;
    document.documentElement.lang = htmlLangFor(lng);
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
